/**
 * INC-329 — "wait for the registered sources, then present once".
 *
 * Once every interrupt is raised AFTER the investor has landed, both sources are asked at
 * the same moment but answer at their own speed: the profile flag may already be cached
 * while the pending vote is still in flight. Without a gate the queue would open the first
 * answer as a bare card and then swap it for the frame when the second arrived — the
 * visible "open then swap" INC-329 exists to prevent.
 *
 * So a round is opened over the sources that were asked, and presentation is held until
 * every one of them has reported — reported meaning "I have an answer", NOT "I queued
 * something". A source with nothing to add still reports, or the round would never close.
 *
 * The timeout is the safety net and belongs to the caller: a source that hangs or fails
 * must never leave a flagged investor with nothing on screen, so the round is released
 * anyway and whatever did arrive is presented.
 *
 * No Angular and no `NgbModal` in here, for the same reason as {@link InterruptQueue} —
 * the rule is testable on its own.
 */
export class InterruptCollector {
  private outstanding = new Set<string>();
  private collecting = false;

  /**
   * Open a round over `sources`. An empty list collects nothing, so presentation is not
   * held — a caller that asks no one is not waiting for anyone.
   */
  begin(sources: string[]): void {
    this.outstanding = new Set(sources);
    this.collecting = this.outstanding.size > 0;
  }

  /**
   * A source has an answer. Unknown keys and late reports after the round closed are
   * ignored rather than reopening it.
   *
   * @returns whether THIS report closed the round (so the caller presents exactly once).
   */
  report(source: string): boolean {
    if (!this.collecting) {
      return false;
    }
    this.outstanding.delete(source);
    if (this.outstanding.size > 0) {
      return false;
    }
    this.collecting = false;
    return true;
  }

  /**
   * End the round without waiting for the stragglers — the timeout, or a session ending
   * underneath us.
   *
   * @returns whether a round was actually open (so a timeout that fires after the last
   *          source reported does not present a second time).
   */
  release(): boolean {
    if (!this.collecting) {
      return false;
    }
    this.collecting = false;
    this.outstanding.clear();
    return true;
  }

  /** True while presentation should be held. */
  get isCollecting(): boolean {
    return this.collecting;
  }

  /** Sources still to report. Diagnostics and tests only. */
  get outstandingCount(): number {
    return this.outstanding.size;
  }
}
